import { readPaymentPeriodAmount } from '../api/paymentPeriod';

const KNOWN_STATUSES = new Set([
  'NOT_AGGREGATED', 'SETTLEMENT_PENDING', 'TRANSFER_PENDING', 'TRANSFERRING',
  'PARTIALLY_TRANSFERRED', 'TRANSFERRED', 'TRANSFER_FAILED', 'CONFIRMING',
  'NO_PAYOUT', 'REVIEW_REQUIRED',
]);

function paymentDateOf(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? value : null;
}

function cpIdOf(value) {
  if (!/^\d+$/.test(String(value ?? ''))) return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

// Amounts are integer KRW. Missing, invalid or unsafe amounts cannot be
// represented as a full group total; a real zero remains a valid value.
function addAmount(total, value) {
  if (total === null) return null;
  const parsed = readPaymentPeriodAmount(value);
  if (!Number.isSafeInteger(parsed)) return null;
  const next = total + parsed;
  return Number.isSafeInteger(next) ? next : null;
}

/** Group fully fetched payment-period rows without changing the server summary. */
export function groupPaymentPeriodRows(rows) {
  if (!Array.isArray(rows)) return [];
  const byDate = new Map();

  rows.forEach((row) => {
    const paymentDate = paymentDateOf(row?.paymentDate);
    if (!byDate.has(paymentDate)) {
      byDate.set(paymentDate, {
        paymentDate,
        rows: [],
        merchantIds: new Set(),
        hasUnknownMerchant: false,
        statusCounts: new Map(),
        amounts: { netPaymentAmount: 0, settlementAmount: 0, transferAmount: 0, unpaidAmount: 0 },
        hasUnaggregated: false,
        hasUnknownSettlement: false,
      });
    }

    const group = byDate.get(paymentDate);
    group.rows.push(row);
    const cpId = cpIdOf(row?.cpId);
    if (cpId === null) group.hasUnknownMerchant = true;
    else group.merchantIds.add(cpId);

    const rawStatus = typeof row?.displayStatus === 'string' ? row.displayStatus.trim().toUpperCase() : '';
    const status = KNOWN_STATUSES.has(rawStatus) ? rawStatus : 'UNKNOWN';
    group.statusCounts.set(status, (group.statusCounts.get(status) || 0) + 1);

    const unaggregated = status === 'NOT_AGGREGATED' || row?.amountState === 'NOT_AGGREGATED';
    group.hasUnaggregated ||= unaggregated;
    if (!unaggregated && !Number.isSafeInteger(readPaymentPeriodAmount(row?.settlementAmounts?.netAmount))) {
      group.hasUnknownSettlement = true;
    }
    group.amounts.netPaymentAmount = addAmount(group.amounts.netPaymentAmount, row?.paymentAmounts?.netPaymentAmount);
    group.amounts.settlementAmount = unaggregated ? null : addAmount(group.amounts.settlementAmount, row?.settlementAmounts?.netAmount);
    group.amounts.transferAmount = addAmount(group.amounts.transferAmount, row?.transferAmount);
    group.amounts.unpaidAmount = unaggregated ? null : addAmount(group.amounts.unpaidAmount, row?.unpaidAmount);
  });

  return Array.from(byDate.values())
    .sort((a, b) => a.paymentDate === null ? 1 : b.paymentDate === null ? -1 : b.paymentDate.localeCompare(a.paymentDate))
    .map(({ merchantIds, hasUnknownMerchant, statusCounts, ...group }) => ({
      ...group,
      merchantCount: hasUnknownMerchant ? null : merchantIds.size,
      rowCount: group.rows.length,
      statuses: Array.from(statusCounts, ([status, count]) => ({ status, count })),
    }));
}
